/* The picker's tree: which rungs exist, which the collection has charts on, and how a
   half-made choice snaps onto a chart that is really there. */

import type { Spot } from "./spot";

// Tree dimensions, in the order they are resolved.
export const DIMS = [
  "stakes",
  "gameType",
  "format",
  "stack",
  "openSize",
  "hero",
  "action",
] as const;
export type Dim = (typeof DIMS)[number];

/* Rungs above `dim`. Written by name because the ladder grows: counting them was right only
   until the day a rung was added above the one being counted. */
export const above = (dim: Dim): number => DIMS.indexOf(dim);

/** Where the picker stands. A null rung is one nobody has chosen yet. `profile` null is the
    chart itself — `Basic Strategy`, the rung the tree stands on by default. */
export interface TreeState extends Record<Dim, string | null> {
  seats: (string | null)[];
  branch: string | null;
  profile: string | null;
}

const uniq = <T, V>(arr: readonly T[], f: (t: T) => V): V[] => [...new Set(arr.map(f))];

/** Spots matching the first `n` dimensions, then `k` seats, then the branch, then the
    profile. The profile is read last, because which profiles exist depends on everything
    above them. */
export function matchTree(
  spots: readonly Spot[],
  state: TreeState,
  n: number,
  k = 0,
  withBranch = false,
  withProfile = false,
): Spot[] {
  return spots.filter(
    (s) =>
      DIMS.slice(0, n).every((d) => s[d] === state[d]) &&
      state.seats.slice(0, k).every((v, i) => s.seats[i] === v) &&
      (!withBranch || !state.branch || (s.branch ?? null) === state.branch) &&
      (!withProfile || (s.profile ?? null) === state.profile),
  );
}

/** The values one rung holds charts for, given the rungs above it. */
export const chartedAt = (spots: readonly Spot[], state: TreeState, dim: Dim): string[] =>
  uniq(matchTree(spots, state, above(dim)), (s) => s[dim]);

/** The seats opponent slot `i` holds charts for, given everything above it. */
export const chartedSeatsAt = (spots: readonly Spot[], state: TreeState, i: number): string[] =>
  uniq(matchTree(spots, state, DIMS.length, i), (s) => s.seats[i]).filter(
    (v): v is string => v !== undefined,
  );

/** The answers charted under the seats chosen. */
export const chartedBranches = (
  spots: readonly Spot[],
  state: TreeState,
  depth: number,
): (string | null)[] => uniq(matchTree(spots, state, DIMS.length, depth), (s) => s.branch ?? null);

/** The profiles charted under everything above them, `null` for the chart itself. The last
    rung of the picker: `Basic Strategy` first, then the collection's profiles. */
export const chartedProfiles = (
  spots: readonly Spot[],
  state: TreeState,
  depth: number,
): (string | null)[] =>
  uniq(matchTree(spots, state, DIMS.length, depth, true), (s) => s.profile ?? null);

// Start on the best-covered open size rather than the first one: a bucket with a single
// chart is work in progress, not a landing screen.
export function richestSize(spots: readonly Spot[]): string | null {
  const n: Record<string, number> = {};
  for (const s of spots) n[s.openSize] = (n[s.openSize] ?? 0) + 1;
  return Object.keys(n).sort((a, b) => (n[b] ?? 0) - (n[a] ?? 0))[0] ?? null;
}

/** Where the tree starts. Nothing charted means nothing to snap onto: the state starts on
    the rungs a first spot is most likely written on, so the creator opens ready to fill in. */
export function initialTree(spots: readonly Spot[]): TreeState {
  const empty = !spots.length;
  return {
    stakes: empty ? "NL10-25" : null,
    gameType: empty ? "Cash Games" : null,
    format: empty ? "6Max" : null,
    stack: empty ? "100bb" : null,
    openSize: richestSize(spots) ?? "2.5x",
    hero: empty ? "LJ" : null,
    action: empty ? "RFI" : null,
    seats: [],
    branch: null,
    // The chart itself, which is where the tree stands until a variation is asked for.
    profile: null,
  };
}

export interface Resolved {
  /** The state, snapped onto a combination that exists. */
  state: TreeState;
  spot: Spot | null;
  /** What was asked for, when it is not what was found. In edit mode the difference is the
      offer to chart the missing spot. */
  missing: TreeState | null;
}

/** Snaps the state onto a combination that actually exists, one dimension at a time. */
export function resolveTree(spots: readonly Spot[], asked: TreeState): Resolved {
  const state: TreeState = { ...asked, seats: [...asked.seats] };
  // Nothing charted: there is no spot to land on and nothing to snap the state onto, so it
  // is left as it stands for the creator to start from.
  if (!spots.length) return { state, spot: null, missing: null };

  DIMS.forEach((d, i) => {
    const opts = uniq(matchTree(spots, state, i), (s) => s[d]);
    const now = state[d];
    if (now === null || !opts.includes(now)) state[d] = opts[0] ?? null;
  });

  const depth = Math.max(0, ...matchTree(spots, state, DIMS.length).map((s) => s.seats.length));
  state.seats.length = depth;
  for (let i = 0; i < depth; i++) {
    const opts = chartedSeatsAt(spots, state, i);
    const now = state.seats[i] ?? null;
    if (now === null || !opts.includes(now)) state.seats[i] = opts[0] ?? null;
  }
  // The branch is read last but one: which answers exist depends on every seat above it.
  const answers = chartedBranches(spots, state, depth);
  if (!answers.includes(state.branch)) state.branch = answers[0] ?? null;

  /* The profile is read after everything else, because which profiles are charted depends on
     all of it. Standing on NIT and moving up the tree keeps NIT wherever a NIT variation
     exists, and snaps back to the chart itself — `Basic Strategy` — where none does. */
  const profiles = chartedProfiles(spots, state, depth);
  if (!profiles.includes(state.profile))
    state.profile = profiles.includes(null) ? null : (profiles[0] ?? null);

  // Dimensions that were overridden — ignoring the ones nobody had chosen yet and the
  // opponent seats the tree appended by itself. A profile snapping back to the chart it
  // deviates from is not a gap to fill: the chart asked for is on screen.
  const snapped =
    DIMS.some((d) => asked[d] != null && asked[d] !== state[d]) ||
    asked.seats.some((v, i) => v != null && v !== state.seats[i]) ||
    (asked.branch != null && asked.branch !== state.branch);

  const spot = matchTree(spots, state, DIMS.length, depth, true, true)[0] ?? null;
  return { state, spot, missing: snapped ? asked : null };
}
